import LoadingSpinner from "../../components/LoadingSpinner";
import React, {ReactNode, useEffect} from "react";
import {getDocumentTitle} from "../../utils/documentTitle";
import {AktuelleSaker} from "../../components/Saker/AktuelleSaker";
import {InnloggetHandlinger} from "../../components/InnloggetHandlinger/InnloggetHandlinger";
import {PktAlert, PktButton, PktIcon} from "@oslokommune/punkt-react";
import {DINE_SAKER_ROUTE} from "../../routes";
import {useNavigate} from "react-router-dom";
import {BarnCardOversikt} from "../../components/FamilieCardOversikt/BarnCardOversikt";
import styles from "./styles.module.scss";
import {classes} from "../../utils/utils";
import {useResoknadStatus} from "../../api/resoknadstatus";
import {usePrissoknader} from "../../api/prissoknader";
import {PrissoknadCardOversikt} from "../../components/FamilieCardOversikt/PrissoknadCardOversikt";
import {useGetBarnBarnehageplassStatus} from "../../api/barnehageplassStatus";
import {PlassertCardOversikt} from "../../components/FamilieCardOversikt/PlassertCardOversikt";

export function DinBarnehageside() {
    const [barnBarnehageplassStatus, barnBarnehagePlassStatusResponseStatus] = useGetBarnBarnehageplassStatus()
    const [resoknadMedlemskap, resoknadMedlemskapResponseStatus] = useResoknadStatus()
    const [prissøknader, prissøknaderResponseStatus] = usePrissoknader()

    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = getDocumentTitle('din-barnehageside')
    })

    const isLoading = () => {
        return (
            resoknadMedlemskapResponseStatus === 'pending' ||
            prissøknaderResponseStatus === 'pending' ||
            barnBarnehagePlassStatusResponseStatus === 'pending'
        )
    }

    const harPlassering = barnBarnehageplassStatus?.some(barn =>
        barn.barnehageplasser && barn.barnehageplasser.length > 0
    ) ?? false

    const harMinstEnAktuellSak = resoknadMedlemskap === 'AKTIV' || barnBarnehageplassStatus?.some(barn => {
        //PLASSERT og UKJENT blir ikke rendra som "aktuelt for deg"
        return barn.statusCard.type !== 'PLASSERT' && barn.statusCard.type !== 'UKJENT'
    })

    if (isLoading()) {
        return (
            <PageContainerWithHeader>
                <LoadingSpinner/>
            </PageContainerWithHeader>
        )
    }

    return (
        <PageContainerWithHeader>
            <PktAlert skin={"info"} title={"Flere digitale tjenester for barnehage er stengt i perioder fram til og" +
              " med 11. november"} closeAlert={true}>
                <p>Vi bytter til nytt barnehagesystem, og flere tjenester vil derfor være utilgjengelige en periode:</p>
                <ul>
                    <li>Søknad om plass, permisjon og oppsigelse: stengt fra 4. november kl. 12.00 til og med 11. november.</li>
                    <li>Søknad om redusert pris: stengt fra 22. oktober til og med 11. november.</li>
                    <li>Tilbud om plass: sendes ikke ut fra 24. oktober til og med 11. november.</li>
                </ul>
                <p>Søknader som allerede er sendt inn, blir overført til det nye systemet.</p>
            </PktAlert>
            <>
                {harMinstEnAktuellSak && (
                    <div className={"mb-size-40 mb-size-104--tablet-up"}>
                        <div className={'mt-size-40 mb-size-20'} style={{display: 'flex', alignItems: 'center'}}>
                            <PktIcon className={'pkt-icon--medium mr-size-12'} name={'megaphone'}/>
                            <h2 className="pkt-txt-22">
                                Aktuelt for deg
                            </h2>
                        </div>
                        <PrissoknadCardOversikt resoknadMedlemskap={resoknadMedlemskap} prissøknader={prissøknader}/>
                        <BarnCardOversikt alleBarnBarnehageplassStatus={barnBarnehageplassStatus}/>
                    </div>
                )}
            </>
            <div>
                <div className={'mt-size-40 mb-size-20'}>
                    <h2 className="pkt-txt-22">
                        Dine barnehageplasser
                    </h2>
                </div>
                {harPlassering ?
                    <div className={"mb-size-40 mb-size-104--tablet-up"}>
                        <PlassertCardOversikt
                            alleBarnBarnehageplassStatus={barnBarnehageplassStatus}></PlassertCardOversikt>
                    </div>
                    :
                    <div className={"mb-size-40"}>
                        <p>
                            <i> Vi finner ingen barnehageplasser registrert på deg i Oslo kommune. Hvis du akkurat har
                                takket ja
                                til en barnehageplass, kan det ta litt tid før den dukker opp her. Du har ingen
                                registrerte
                                barnehageplasser. </i>
                        </p>
                    </div>
                }
            </div>
            <div className={"mb-size-40 mb-size-104--tablet-up"}>
                <AktuelleSaker/>
                <SeAlleSakerLink/>
            </div>
            <div className={"mb-size-40 mb-size-104--tablet-up"}>
                <InnloggetHandlinger resoknadMedlemskap={resoknadMedlemskap}/>
            </div>
        </PageContainerWithHeader>
    )
}

const PageContainerWithHeader = ({children}: { children: ReactNode }) => (
    <div className="pkt-cell pkt-cell--span12">
        <div
            className="my-size-24 mt-size-64--tablet-up mb-size-40--tablet-up">
            <h1 className="pkt-txt-36 pkt-txt-40--tablet-up mb-size-24 mb-size-40--tablet-up">
                Din barnehageside
            </h1>
            {children}
        </div>
    </div>
)

const SeAlleSakerLink = () => {
    const navigate = useNavigate()
    return (
        <div className={classes(styles.seAlleSakerContainer, "mt-size-40")}>
            <PktButton type={"button"} skin={"tertiary"} iconName={"chevron-thin-right"} variant={"icon-right"}
                       onClick={() => navigate(DINE_SAKER_ROUTE)}>
                Se avsluttede saker
            </PktButton>
        </div>
    )
}
